/* eslint-disable no-console */
import { getRefreshToken } from "./getRefreshToken";
import { NavigateFunction } from "react-router-dom";
import { AxiosError } from "axios";
import { getCookie } from "./getCookie";
import { logger } from "../../utils/logger";
import { authStateManager } from "./authStateManager";
import { handleRedirect } from "../pages/Login";

// Helper function to debounce alerts - Keep this for non-network errors
const showDebouncedAlert = (
  message: string,
  alertType: string,
  debounceTime = 5000
) => {
  const now = Date.now();
  const lastAlertTime = parseInt(
    localStorage.getItem(`lastAlert_${alertType}`) || "0"
  );

  // Only show alert if enough time has passed since the last one of this type
  if (now - lastAlertTime > debounceTime) {
    localStorage.setItem(`lastAlert_${alertType}`, now.toString());
    alert(message);
  }
};

/** Returns the response body as a string for safe .includes() checks. Prefers reason.response.data, fallback to reason.request.response. */
function getResponseBodyString(reason: AxiosError): string {
  try {
    if (reason.response?.data != null) {
      return typeof reason.response.data === "string"
        ? reason.response.data
        : JSON.stringify(reason.response.data);
    }
    const req = reason.request as { response?: string } | undefined;
    if (req?.response != null && typeof req.response === "string") {
      return req.response;
    }
  } catch {
    // ignore
  }
  return "";
}

/**
 * 403 bodies that mean "you may not do this", which no retry or token refresh
 * can change. actuate_admin returns the View Only one on every write for View
 * Only users (ENG-1911); the other is DRF's default denial. Without this they
 * fell into the transient-network path below: three retries with backoff, then
 * the caller's generic "please try again" (ENG-1975).
 */
export const PERMISSION_DENIED_MESSAGES: Record<string, string> = {
  "View Only users can view but not make changes.":
    "Your account is view only, so this change wasn't saved. Ask your administrator if you need to make changes.",
  "You do not have permission to perform this action.":
    "You don't have permission to do this. Ask your administrator if you need access.",
  // actuate_admin's group-user grant guard (ENG-1326): shown as written.
  "You can only grant permissions and access levels that you hold yourself, and cannot edit your own account":
    "You can only grant permissions and access levels that you hold yourself, and you can't edit your own account. Ask another administrator to make this change.",
};

/**
 * Only a denied WRITE gets the dialog. Pages fire many reads on open (Edit
 * Camera alone makes a dozen) and a level that may not read one of them is
 * routine, not something the user can act on; before ENG-1975 those denials
 * were silent and the page still worked (SUP-688, ENG-2014). GET 403s take the
 * old path below: retry, then the caller's own handler.
 */
const isReadRequest = (reason: AxiosError): boolean =>
  String(reason.config?.method ?? "").toLowerCase() === "get";

const permissionDeniedMessage = (reason: AxiosError): string | null => {
  if (isReadRequest(reason)) return null;
  const data = reason.response?.data as { detail?: unknown } | undefined;
  const detail = typeof data?.detail === "string" ? data.detail : null;
  return (detail && PERMISSION_DENIED_MESSAGES[detail]) || null;
};

/**
 * Passed to `errorHandlingFunction` so handlers can tell a terminal failure
 * apart from one where a retry is still pending or the browser is offline.
 * Handlers that take no parameters are unaffected.
 */
export interface APIErrorContext {
  /** An automatic retry has been scheduled; the failure is not yet terminal. */
  willRetry?: boolean;
  /** The browser is offline and the user is being redirected to /no-internet. */
  offline?: boolean;
}

export const universalAPIErrorHandler = (
  reason: AxiosError,
  navigate: NavigateFunction,
  retryFunction: () => void,
  errorHandlingFunction: (context?: APIErrorContext) => void
) => {
  const token = getCookie("token");
  const refreshToken = getCookie("token_secret");

  // Log the error for debugging
  logger.error("API Error:", reason.message);

  // --- Handle 403 token errors BEFORE offline check so we always refresh when we can ---
  if (reason.response?.status === 403) {
    // A permission denial is final: tell the user once and stop.
    const denied = permissionDeniedMessage(reason);
    if (denied) {
      showDebouncedAlert(denied, "permission_denied");
      if (errorHandlingFunction) {
        errorHandlingFunction();
      }
      return;
    }

    const body = getResponseBodyString(reason);

    if (body.includes("Invalid token")) {
      const refreshTokenForInvalid = getCookie("token_secret");
      if (refreshTokenForInvalid) {
        logger.info("Attempting token refresh in background");
        localStorage.setItem("tokenRefreshing", "true");
        getRefreshToken(navigate, () => {
          logger.info("Token refresh successful, retrying original request");
          localStorage.setItem("tokenRefreshing", "false");
          if (retryFunction) retryFunction();
        });
        return;
      }
      logger.warn("No refresh token available, redirecting to Cognito");
      authStateManager.clearAuthState();
      const currentPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      localStorage.setItem("redirectURL", currentPath);
      handleRedirect();
      return;
    }

    const isTokenErrorBody =
      body.includes("SocialToken matching query does not exist") ||
      body.includes("Returned invalid token from cache") ||
      body.includes("Token is expired") ||
      body.includes("Token not found") ||
      Boolean(!token && refreshToken) === true;

    if (isTokenErrorBody) {
      const isInAuthFlow = window.location.pathname.includes("/callback");
      if (isInAuthFlow) {
        logger.info("In authentication flow, skipping token refresh");
        return;
      }
      if (refreshToken) {
        logger.info("Token refresh needed, attempting refresh");
        localStorage.setItem("tokenRefreshing", "true");
        getRefreshToken(navigate, retryFunction);
        return;
      }
      logger.warn("No refresh token available, redirecting to Cognito");
      authStateManager.clearAuthState();
      const currentPath =
        window.location.pathname +
        window.location.search +
        window.location.hash;
      localStorage.setItem("redirectURL", currentPath);
      handleRedirect();
      return;
    }
  }

  // --- Check browser online status (after 403 token handling) ---
  if (!navigator.onLine) {
    logger.log("Browser is offline. Redirecting to /no-internet");
    if (window.location.pathname !== "/no-internet") {
      navigate("/no-internet", { replace: true });
    }
    if (errorHandlingFunction) {
      errorHandlingFunction({ offline: true });
    }
    return;
  }

  if (reason.response) {
    // Handle specific HTTP error status codes
    const status = reason.response.status;

    if (status === 403) {
      logger.log("Network Error - Connection issue detected");

      const body = getResponseBodyString(reason);
      if (body.includes("Invalid token")) {
        // Check if we have a refresh token available
        const refreshToken = getCookie("token_secret");

        if (refreshToken) {
          logger.info("Attempting token refresh in background");
          localStorage.setItem("tokenRefreshing", "true");

          // Try to refresh the token
          getRefreshToken(navigate, () => {
            logger.info("Token refresh successful, retrying original request");
            localStorage.setItem("tokenRefreshing", "false");
            if (retryFunction) {
              retryFunction();
            }
          });
          return;
        } else {
          logger.warn("No refresh token available, redirecting to Cognito");
          // Clear all auth state and redirect to Cognito
          authStateManager.clearAuthState();
          // Store only the path portion for security and proper handling
          const currentPath =
            window.location.pathname +
            window.location.search +
            window.location.hash;
          localStorage.setItem("redirectURL", currentPath);
          handleRedirect();
          return;
        }
      }

      if (
        body.includes("SocialToken matching query does not exist") ||
        body.includes("Returned invalid token from cache") ||
        body.includes("Token is expired") ||
        body.includes("Token not found") ||
        Boolean(!token && refreshToken) === true
      ) {
        // Check if we're in the middle of authentication flow (Callback)
        const isInAuthFlow = window.location.pathname.includes("/callback");

        if (isInAuthFlow) {
          logger.info("In authentication flow, skipping token refresh");
          return;
        }

        if (refreshToken) {
          logger.info("Token refresh needed, attempting refresh");
          localStorage.setItem("tokenRefreshing", "true");
          getRefreshToken(navigate, retryFunction);
          return;
        } else {
          logger.warn("No refresh token available, redirecting to Cognito");
          authStateManager.clearAuthState();
          // Store only the path portion for security and proper handling
          const currentPath =
            window.location.pathname +
            window.location.search +
            window.location.hash;
          localStorage.setItem("redirectURL", currentPath);
          handleRedirect();
          return;
        }
      }

      // Implement retry logic for transient network issues
      const retryCount = parseInt(
        localStorage.getItem("networkErrorRetryCount") || "0"
      );
      if (retryCount < 3) {
        localStorage.setItem(
          "networkErrorRetryCount",
          (retryCount + 1).toString()
        );
        logger.log(`Retrying request (${retryCount + 1}/3)...`);

        // Wait before retrying (exponential backoff)
        setTimeout(() => {
          retryFunction();
          logger.log("power of 2", 1000 * Math.pow(2, retryCount));
        }, 1000 * Math.pow(2, retryCount));

        return;
      }

      // Reset retry counter after max attempts
      localStorage.setItem("networkErrorRetryCount", "0");

      // Only redirect to no-internet when the browser is actually offline (!navigator.onLine).
      // Do not redirect for 403 or empty response — those are auth/server issues, not connectivity.
      if (errorHandlingFunction) {
        errorHandlingFunction();
      }
    } else if (status === 401) {
      // Unauthorized - token issues
      localStorage.setItem("tokenRefreshing", "true");
      getRefreshToken(navigate, retryFunction);
    } else if (status >= 500) {
      // Server errors
      // Server errors - don't retry, just handle the error
      // showDebouncedAlert(
      //   "The server encountered an error. Our team has been notified.",
      //   "server_error"
      // );
      if (errorHandlingFunction) {
        errorHandlingFunction();
      }
      // Explicitly return to prevent any retry logic from executing
      return;
    } else if (status === 404) {
      // Resource not found
      // showDebouncedAlert("The requested resource was not found", "not_found_error");
      if (errorHandlingFunction) {
        errorHandlingFunction();
      }
    } else if (status === 429) {
      // Rate limiting
      logger.log("429 - Too many requests. Retrying after: 30 seconds");
      setTimeout(() => {
        retryFunction();
      }, 30000);
      // showDebouncedAlert(
      //   `Too many requests. Please try again in ${retryAfter} seconds.`,
      //   "rate_limit_error"
      // );
      if (errorHandlingFunction) {
        errorHandlingFunction({ willRetry: true });
      }
    } else {
      // Handle other status codes

      if (
        reason.response?.data &&
        typeof reason.response.data === "object" &&
        "group" in reason.response.data
      ) {
        logger.log(
          `Error: ${(reason.response.data as { group: string[] }).group?.[0]}`
        ); // Log specific error if available
      } else {
        logger.log(
          "Error: Unable to retrieve specific error details. Running error handling functionality for this API call."
        ); // Fallback log
      }
      if (errorHandlingFunction) {
        errorHandlingFunction();
      }
    }
  } else {
    // Fallback for other errors (e.g., request setup errors)
    logger.log(`Unhandled Error: ${reason}`);
    // showDebouncedAlert("An unexpected error occurred", "unexpected_error");
    if (errorHandlingFunction) {
      errorHandlingFunction();
    }
  }
};
